import { useEffect, useMemo, useState } from 'react';
import { useElectionDayEditorial } from '../lib/useElectionDayEditorial';
import './NewsTicker.css';

// Headline shape includes a thumbnail URL so the ticker can render the
// article image alongside the title (Phase 1 ticker upgrade).
// `kind: 'breaking'` items render with a party-coloured background
// and a "BREAKING" prefix — currently driven by the editorial
// nationalCall (winner announcement). Always sit first.
interface Headline {
  title: string;
  url: string;
  thumbnail: string | null;
  kind?: 'breaking-pl' | 'breaking-pn';
}

const PARTY_FULL_NAME: Record<'PL' | 'PN', string> = {
  PL: 'Labour Party',
  PN: 'Nationalist Party',
};

// Fallback headlines if /api/news/latest is unavailable. The ticker keeps
// rendering something useful even on a cold cache or while the function
// warms up.
const FALLBACK_HEADLINES: Headline[] = [
  {
    title: 'Alex Borg pledges mass transport system operational within five years',
    url: 'https://lovinmalta.com/news/general-election-2026/alex-borg-pledges-mass-transport-system-operational-within-five-years/',
    thumbnail: null,
  },
  {
    title: 'Alex Borg hits the road as PN launches new mobile office',
    url: 'https://lovinmalta.com/news/general-election-2026/alex-borg-hits-the-road-as-pn-launches-new-mobile-office/',
    thumbnail: null,
  },
  {
    title: 'PL proposes €5,000 individual learning accounts for every child',
    url: 'https://lovinmalta.com/news/general-election-2026/pl-proposes-e5000-individual-learning-accounts-for-every-child/',
    thumbnail: null,
  },
  {
    title: 'PL proposes right to request remote working and flexible hours',
    url: 'https://lovinmalta.com/news/general-election-2026/pl-proposes-right-to-request-remote-working-and-flexible-hours/',
    thumbnail: null,
  },
  {
    title: 'WATCH: Roberta Metsola endorses Alex Borg ahead of general election',
    url: 'https://lovinmalta.com/news/news-politics/watch-roberta-metsola-endorses-alex-borg-ahead-of-general-election/',
    thumbnail: null,
  },
  {
    title: 'PL pledges to double grant for first-time buyers with severe disabilities to €20,000',
    url: 'https://lovinmalta.com/news/news-politics/pl-pledges-to-double-grant-for-first-time-buyers-with-severe-disabilities-to-e20000/',
    thumbnail: null,
  },
];

const TICKER_COUNT = 6;

export default function NewsTicker() {
  const [headlines, setHeadlines] = useState<Headline[]>(FALLBACK_HEADLINES);
  const { data: editorial } = useElectionDayEditorial();

  // When the newsroom has called the national result, prepend a
  // BREAKING item at the front of the ticker. The breaking item
  // takes the party's colour (PL red, PN blue) and a different
  // typographic treatment so readers see it first regardless of
  // where the marquee is mid-scroll.
  const decorated = useMemo<Headline[]>(() => {
    const winner = editorial?.nationalCall?.party;
    if (!winner) return headlines;
    const breaking: Headline = {
      title: `${PARTY_FULL_NAME[winner]} Wins General Election`,
      url: '/',
      thumbnail: null,
      kind: winner === 'PL' ? 'breaking-pl' : 'breaking-pn',
    };
    return [breaking, ...headlines];
  }, [headlines, editorial]);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    fetch('/api/news/latest', { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(
        (data: {
          articles?: { title: string; link: string; thumbnail: string | null }[];
        }) => {
          if (cancelled) return;
          if (Array.isArray(data.articles) && data.articles.length > 0) {
            setHeadlines(
              data.articles.slice(0, TICKER_COUNT).map((a) => ({
                title: a.title,
                url: a.link,
                thumbnail: a.thumbnail ?? null,
              })),
            );
          }
        },
      )
      .catch((err) => {
        if (cancelled || err?.name === 'AbortError') return;
        // Keep the fallback list — the ticker should never go blank.
        console.warn('[NewsTicker] live fetch failed, using fallback:', err);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  // Render the headline list twice so the CSS marquee can translate by -50%
  // and seamlessly loop back to the start without a visible jump.
  const loop = [...decorated, ...decorated];

  return (
    <div className="news-ticker" role="region" aria-label="Latest election news">
      <span className="news-ticker__label">
        <span className="news-ticker__pulse" aria-hidden="true" />
        Latest
      </span>
      <div className="news-ticker__viewport">
        <div className="news-ticker__track">
          {loop.map((h, i) => {
            const isBreaking = h.kind === 'breaking-pl' || h.kind === 'breaking-pn';
            const itemClass = isBreaking
              ? `news-ticker__item news-ticker__item--${h.kind}`
              : 'news-ticker__item';
            const isInternal = h.url.startsWith('/');
            return (
              <a
                key={`${i}-${h.url}`}
                href={h.url}
                target={isInternal ? '_self' : '_blank'}
                rel={isInternal ? undefined : 'noopener noreferrer'}
                className={itemClass}
                aria-hidden={i >= decorated.length ? 'true' : undefined}
                tabIndex={i >= decorated.length ? -1 : 0}
              >
                {isBreaking ? (
                  <span className="news-ticker__breaking-tag" aria-hidden="true">
                    BREAKING
                  </span>
                ) : h.thumbnail ? (
                  /* `no-referrer` bypasses Lovin Malta's CDN hotlink check —
                     same trick the ArticleTile component uses. */
                  <img
                    className="news-ticker__thumb"
                    src={h.thumbnail}
                    alt=""
                    loading="lazy"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="news-ticker__thumb news-ticker__thumb--placeholder" aria-hidden="true" />
                )}
                <span className="news-ticker__title">{h.title}</span>
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
